/**
 * CoffeeClubTab - Admin dashboard for Bula Coffee Pass reporting
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Coffee, Users, TrendingUp, DollarSign, Clock, Calendar,
  AlertTriangle, CheckCircle, BarChart3, PieChart, Download,
  RefreshCw, ChevronRight, Star, Gift, Share2, Trash2, X, RefreshCcw,
  Search, Phone, Mail, Edit2, Save, Eye, Merge, UserCog, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Cancel reasons
const CANCEL_REASONS = [
  "Customer requested cancellation",
  "Duplicate purchase",
  "Payment issue",
  "Customer never received pass",
  "Fraudulent transaction",
  "Staff error",
  "Other"
];

// Refund methods
const REFUND_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card (Original Payment)" },
  { value: "mpaisa", label: "M-PAiSA" },
  { value: "store_credit", label: "Store Credit" }
];

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendUp, className = "" }) => (
  <Card className={className}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold text-foreground">{value}</p>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          )}
          {trend && (
            <p className={`text-sm mt-2 ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
              {trendUp ? '↑' : '↓'} {trend}
            </p>
          )}
        </div>
        <div className="p-3 rounded-xl bg-primary/10">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Progress Bar Component
const ProgressBar = ({ label, value, max, color = "primary" }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${
            color === 'primary' ? 'bg-primary' : 
            color === 'amber' ? 'bg-amber-500' : 
            color === 'green' ? 'bg-green-500' : 'bg-blue-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

export default function CoffeeClubTab() {
  const [report, setReport] = useState(null);
  const [passes, setPasses] = useState([]);
  const [giftStats, setGiftStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingPassId, setDeletingPassId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  // Cancel/Refund modal state
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Member Management state
  const [activeSection, setActiveSection] = useState('analytics'); // 'analytics' or 'members'
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  
  // Sync state for fixing dashboard data
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  // Comprehensive member report state
  const [comprehensiveReport, setComprehensiveReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loadingComprehensive, setLoadingComprehensive] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // Complimentary Pass state
  const [showCompModal, setShowCompModal] = useState(false);
  const [compSearchQuery, setCompSearchQuery] = useState('');
  const [compSearchResults, setCompSearchResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [creatingComp, setCreatingComp] = useState(false);

  // Search contacts for comp pass
  const handleCompSearch = async () => {
    if (!compSearchQuery || compSearchQuery.length < 3) {
      toast.error('Enter at least 3 characters to search');
      return;
    }
    
    setSearchingContacts(true);
    try {
      const res = await api.get(`/contacts/search?q=${encodeURIComponent(compSearchQuery)}`);
      setCompSearchResults(res.data.contacts || res.data || []);
      if ((res.data.contacts || res.data || []).length === 0) {
        toast.info('No contacts found');
      }
    } catch (err) {
      console.error('Search failed:', err);
      toast.error('Search failed');
    }
    setSearchingContacts(false);
  };

  // Create complimentary pass for selected contact
  const handleGiveCompPass = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact first');
      return;
    }
    
    // Validate contact has required fields
    const contactEmail = selectedContact.email;
    const contactPhone = selectedContact.phone;
    
    if (!contactEmail && !contactPhone) {
      toast.error('Contact must have an email or phone number');
      return;
    }
    
    if (!contactPhone) {
      toast.error('Contact must have a phone number for Coffee Pass');
      return;
    }
    
    setCreatingComp(true);
    try {
      const firstName = selectedContact.first_name || selectedContact.name?.split(' ')[0] || 'Customer';
      const lastName = selectedContact.last_name || selectedContact.name?.split(' ').slice(1).join(' ') || '';
      
      const res = await api.post('/coffee-club/admin/comp-pass', {
        first_name: firstName,
        last_name: lastName,
        email: contactEmail || `${contactPhone}@noemail.bulaccino.com`,
        phone: contactPhone
      });
      
      toast.success(`Pass created for ${selectedContact.name || contactEmail || contactPhone}!`, {
        description: `Expires: ${res.data?.pass?.expiry_date || '365 days'}`
      });
      setShowCompModal(false);
      setSelectedContact(null);
      setCompSearchQuery('');
      setCompSearchResults([]);
      // Refresh report data
      try {
        await fetchReport();
      } catch (refreshErr) {
        console.log('Report refresh skipped');
      }
    } catch (err) {
      console.error('Failed to create comp pass:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create pass';
      toast.error(errorMsg);
    } finally {
      setCreatingComp(false);
    }
  };

  // Fetch comprehensive members report
  const fetchComprehensiveReport = async () => {
    setLoadingComprehensive(true);
    try {
      const res = await api.get('/coffee-club/admin/members-comprehensive-report', {
        params: { month: selectedMonth, year: selectedYear }
      });
      setComprehensiveReport(res.data);
    } catch (err) {
      console.error('Failed to fetch comprehensive report:', err);
      toast.error('Failed to load member analytics');
    }
    setLoadingComprehensive(false);
  };

  // Export members CSV
  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const response = await api.get('/coffee-club/admin/export-members-csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bula_coffee_pass_members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Members exported successfully!');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export members');
    }
    setExportingCsv(false);
  };

  // Run data migration to fix analytics
  const handleRunMigration = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/coffee-club/admin/migrate-redemption-analytics');
      toast.success('Analytics data fixed!', {
        description: `Updated ${res.data.updated} records. Refresh to see new data.`
      });
      // Refresh the report data
      await fetchReport();
    } catch (err) {
      console.error('Migration error:', err);
      toast.error('Failed to fix data. Please try again.');
    }
    setMigrating(false);
  };

  const resetCancelForm = () => {
    setCancelReason('');
    setRefundMethod('');
    setCancelNotes('');
  };

  // Cancel Coffee Pass with cascading logic
  const handleCancelPass = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please select a reason for cancellation');
      return;
    }
    
    setProcessing(true);
    try {
      // Use the sale_id if available, otherwise use _id
      const saleId = showCancelModal.sale_id || showCancelModal._id;
      const res = await api.post(`/admin/coffee-pass/${saleId}/cancel`, {
        reason: cancelReason,
        refund_method: refundMethod || null,
        notes: cancelNotes || null
      });
      
      toast.success('Coffee Pass cancelled', {
        description: `Refund: $${res.data.refund_amount?.toFixed(2) || '0.00'}. Logged to cancellations report.`
      });
      setShowCancelModal(null);
      resetCancelForm();
      await loadData(); // Refresh all data
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel pass');
    }
    setProcessing(false);
  };

  const fetchReport = async () => {
    try {
      const res = await api.get('/coffee-club/admin/report');
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch coffee club report:', err);
      toast.error('Failed to load report');
    }
  };

  const fetchPasses = async () => {
    try {
      const res = await api.get('/coffee-club/admin/passes', { params: { limit: 50 } });
      setPasses(res.data.passes || []);
    } catch (err) {
      console.error('Failed to fetch passes:', err);
    }
  };

  const fetchGiftStats = async () => {
    try {
      const res = await api.get('/coffee-club/admin/gifts', { params: { limit: 5 } });
      setGiftStats(res.data);
    } catch (err) {
      console.error('Failed to fetch gift stats:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchReport(), fetchPasses(), fetchGiftStats(), fetchDuplicates(), fetchComprehensiveReport()]);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Member Management Functions
  const fetchDuplicates = async () => {
    try {
      const res = await api.get('/coffee-pass/admin/duplicate-phones');
      setDuplicates(res.data.duplicates || []);
    } catch (err) {
      console.error('Failed to check duplicates:', err);
    }
  };

  const handleMemberSearch = async (e) => {
    e?.preventDefault();
    if (!memberSearch.trim()) {
      toast.error('Please enter a phone number, email, or name to search');
      return;
    }
    setSearchLoading(true);
    try {
      const res = await api.get(`/coffee-pass/admin/members?search=${encodeURIComponent(memberSearch)}&limit=20`);
      setSearchResults(res.data.members || []);
      if (res.data.members?.length === 0) {
        toast.info('No members found matching your search');
      }
    } catch (err) {
      console.error('Search failed:', err);
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setEditForm({
      phone: member.phone || '',
      email: member.email || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      status: member.status || 'active'
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMember) return;
    setSaving(true);
    try {
      await api.put(`/coffee-pass/admin/members/${editingMember._id}`, editForm);
      toast.success('Member updated successfully');
      setEditModal(false);
      // Refresh search and duplicates
      if (memberSearch) handleMemberSearch();
      fetchDuplicates();
      fetchPasses();
    } catch (err) {
      console.error('Failed to update member:', err);
      toast.error(err.response?.data?.detail || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleMergeMembers = async (keepId, deleteId, keepName, deleteName) => {
    if (!confirm(`Merge "${deleteName}" into "${keepName}"? This will transfer redemptions and delete the duplicate account.`)) {
      return;
    }
    try {
      await api.post(`/coffee-pass/admin/members/merge?keep_member_id=${keepId}&delete_member_id=${deleteId}`);
      toast.success('Members merged successfully');
      fetchDuplicates();
      fetchPasses();
    } catch (err) {
      console.error('Failed to merge members:', err);
      toast.error(err.response?.data?.detail || 'Failed to merge members');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-FJ', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Delete pass permanently (removes pass, sales record, and redemptions)
  const handleDeletePass = async (passId, passName) => {
    setDeletingPassId(passId);
    try {
      await api.delete(`/coffee-club/admin/passes/${passId}`, { params: { permanent: true } });
      
      toast.success(`Pass deleted`, {
        description: `${passName}'s pass and all associated records have been permanently removed.`
      });
      // Refresh all data to update stats
      await loadData();
    } catch (err) {
      console.error('Failed to delete pass:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete pass');
    } finally {
      setDeletingPassId(null);
      setConfirmDelete(null);
    }
  };

  // Sync dashboard data - fixes missing transactions and names
  const handleSyncDashboard = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      // Run the sync
      const res = await api.post('/coffee-club/admin/sync-transactions');
      setSyncResult(res.data);
      
      if (res.data.synced > 0) {
        toast.success(`Synced ${res.data.synced} missing records!`, {
          description: 'Dashboard should now show correct data. Refreshing...'
        });
        // Refresh the data
        await loadData();
      } else {
        toast.info('All data is already synced - no missing records found.');
      }
    } catch (err) {
      console.error('Sync failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = report?.summary || {};
  const redemptions = report?.redemptions || {};
  const basketAnalysis = report?.basket_analysis || {};
  const roiAnalysis = report?.roi_analysis || {};
  const heavyUsers = report?.heavy_users || {};
  const byChannel = report?.by_channel || {};
  const byHour = report?.by_hour || {};
  const dailyBreakdown = report?.daily_breakdown || [];

  return (
    <div className="space-y-6" data-testid="coffee-club-admin-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary/10">
            <Coffee className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading">Bula Coffee Pass</h2>
            <p className="text-sm text-muted-foreground">
              {activeSection === 'analytics' ? 'Analytics & Reporting' : 'Member Management'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Sync Dashboard Data Button */}
          <Button 
            variant="outline"
            onClick={handleSyncDashboard}
            disabled={syncing}
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="sync-dashboard-btn"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4 mr-2" />
            )}
            {syncing ? 'Syncing...' : 'Sync Dashboard Data'}
          </Button>
          {/* Fix Analytics Data Button */}
          {activeSection === 'analytics' && report?.redemptions?.standalone === 0 && report?.redemptions?.with_purchase === 0 && report?.redemptions?.total > 0 && (
            <Button 
              variant="outline"
              onClick={handleRunMigration}
              disabled={migrating}
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
            >
              {migrating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              {migrating ? 'Fixing...' : 'Fix Analytics Data'}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {/* Gift Complimentary Pass Button */}
          <Button 
            onClick={() => setShowCompModal(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="gift-comp-pass-btn"
          >
            <Gift className="w-4 h-4 mr-2" />
            Gift Comp Pass
          </Button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        <Button
          variant={activeSection === 'analytics' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveSection('analytics')}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Analytics
        </Button>
        <Button
          variant={activeSection === 'members' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveSection('members')}
        >
          <UserCog className="w-4 h-4 mr-2" />
          Manage Members
          {duplicates.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
              {duplicates.length}
            </span>
          )}
        </Button>
      </div>

      {/* Member Management Section */}
      {activeSection === 'members' && (
        <div className="space-y-6">
          {/* Duplicate Phone Alert */}
          {duplicates.length > 0 && (
            <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-5 h-5" />
                  {duplicates.length} Duplicate Phone Number{duplicates.length > 1 ? 's' : ''} Found
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  These phone numbers are associated with multiple accounts. Fix them by editing or merging.
                </p>
                {duplicates.map((dup, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Phone className="w-4 h-4 text-amber-600" />
                      <span className="font-mono font-semibold">{dup._id}</span>
                      <span className="text-sm text-muted-foreground">({dup.count} accounts)</span>
                    </div>
                    <div className="space-y-2">
                      {dup.members.map((m, mIdx) => (
                        <div key={mIdx} className="flex items-center justify-between bg-muted/50 rounded p-2">
                          <div>
                            <span className="font-medium">{m.first_name} {m.last_name}</span>
                            <span className="text-sm text-muted-foreground ml-2">({m.email || 'no email'})</span>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditModal({_id: m.id, ...m})}>
                              <Edit2 className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                setConfirmDelete(m.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {dup.members.length === 2 && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleMergeMembers(
                            dup.members[0].id, 
                            dup.members[1].id,
                            `${dup.members[0].first_name} ${dup.members[0].last_name}`,
                            `${dup.members[1].first_name} ${dup.members[1].last_name}`
                          )}
                        >
                          <Merge className="w-3 h-3 mr-1" /> Keep First, Delete Second
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleMergeMembers(
                            dup.members[1].id, 
                            dup.members[0].id,
                            `${dup.members[1].first_name} ${dup.members[1].last_name}`,
                            `${dup.members[0].first_name} ${dup.members[0].last_name}`
                          )}
                        >
                          <Merge className="w-3 h-3 mr-1" /> Keep Second, Delete First
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Search Members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Search Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleMemberSearch} className="flex gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search by phone number, email, or name..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={searchLoading}>
                  {searchLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2">Search</span>
                </Button>
              </form>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-muted-foreground">Found {searchResults.length} member(s)</p>
                  <div className="border rounded-lg divide-y">
                    {searchResults.map((member) => (
                      <div key={member._id} className="p-3 flex items-center justify-between hover:bg-muted/50">
                        <div>
                          <p className="font-medium">{member.first_name} {member.last_name}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {member.phone}</span>
                            {member.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {member.email}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              member.status === 'active' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                              {member.status || 'active'}
                            </span>
                            <span className="text-xs text-muted-foreground">Expires: {formatDate(member.expiry_date)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEditModal(member)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => setConfirmDelete(member._id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Section */}
      {activeSection === 'analytics' && (
        <>
          {/* Key Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Passes Sold"
          value={summary.total_passes_sold || 0}
          subtitle={`${summary.active_passes || 0} active`}
          icon={Users}
        />
        <StatCard
          title="Total Revenue"
          value={`$${(summary.total_revenue || 0).toFixed(0)}`}
          subtitle="FJD from passes"
          icon={DollarSign}
        />
        <StatCard
          title="Today's Redemptions"
          value={redemptions.today || 0}
          subtitle={`${redemptions.total || 0} total all-time`}
          icon={Coffee}
          className="border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20"
        />
        <StatCard
          title="Net Value"
          value={`$${(roiAnalysis.net_value || 0).toFixed(0)}`}
          subtitle={roiAnalysis.profitable ? 'Profitable' : 'Needs attention'}
          icon={TrendingUp}
          className={roiAnalysis.profitable ? 'border-green-500/30' : 'border-red-500/30'}
        />
      </div>

      {/* Daily Redemption Trend */}
      <Card className="border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-600" />
            Daily Redemptions (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyBreakdown.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {dailyBreakdown.map((day) => (
                <div key={day.date} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-sm w-24">{day.date}</span>
                    <div className="flex gap-4 text-sm">
                      <span className="text-muted-foreground">
                        <span className="font-bold text-foreground">{day.redemptions}</span> total
                      </span>
                      {day.with_purchase > 0 && (
                        <span className="text-green-600">
                          {day.with_purchase} with purchase
                        </span>
                      )}
                      {day.upgrades > 0 && (
                        <span className="text-blue-600">
                          {day.upgrades} upgrades
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-24 bg-muted rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 rounded-full transition-all"
                      style={{ width: `${Math.min((day.redemptions / 20) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No redemptions recorded yet. Redemptions will appear here once customers start using their Bula Coffee Passes.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Redemption Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Redemption Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Redemption Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                <div>
                  <p className="font-medium">Standalone Redemptions</p>
                  <p className="text-sm text-muted-foreground">Free coffee only, no purchase</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{redemptions.standalone || 0}</p>
                  <p className="text-sm text-muted-foreground">{redemptions.standalone_percentage || 0}%</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                <div>
                  <p className="font-medium text-green-700 dark:text-green-400">With Purchase</p>
                  <p className="text-sm text-muted-foreground">Free coffee + paid items</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400">{redemptions.with_purchase || 0}</p>
                  <p className="text-sm text-muted-foreground">{redemptions.with_purchase_percentage || 0}%</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Avg Basket Value (with purchase)</span>
                  <span className="font-bold text-lg">${basketAnalysis.average_basket_value || 0} FJD</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-muted-foreground">Total Additional Revenue</span>
                  <span className="font-bold text-lg text-green-600">${basketAnalysis.total_additional_revenue || 0} FJD</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ROI Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              ROI Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <ProgressBar
                label="Pass Revenue"
                value={roiAnalysis.pass_revenue || 0}
                max={Math.max(roiAnalysis.pass_revenue || 0, roiAnalysis.estimated_coffee_cost || 0, roiAnalysis.additional_purchase_revenue || 0)}
                color="primary"
              />
              <ProgressBar
                label="Additional Purchase Revenue"
                value={roiAnalysis.additional_purchase_revenue || 0}
                max={Math.max(roiAnalysis.pass_revenue || 0, roiAnalysis.estimated_coffee_cost || 0, roiAnalysis.additional_purchase_revenue || 0)}
                color="green"
              />
              <ProgressBar
                label="Est. Coffee Cost"
                value={roiAnalysis.estimated_coffee_cost || 0}
                max={Math.max(roiAnalysis.pass_revenue || 0, roiAnalysis.estimated_coffee_cost || 0, roiAnalysis.additional_purchase_revenue || 0)}
                color="amber"
              />

              <div className={`p-4 rounded-xl ${roiAnalysis.profitable ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {roiAnalysis.profitable ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-medium">Net Value</span>
                  </div>
                  <span className={`text-2xl font-bold ${roiAnalysis.profitable ? 'text-green-600' : 'text-red-600'}`}>
                    ${(roiAnalysis.net_value || 0).toFixed(0)} FJD
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Redemption by Channel & Hour */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Channel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Redemptions by Channel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(byChannel).length > 0 ? (
                Object.entries(byChannel).map(([channel, count]) => (
                  <div key={channel} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="capitalize">{channel}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No redemptions yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Hour */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Redemptions by Hour (7-10 AM)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(byHour).length > 0 ? (
                Object.entries(byHour).sort().map(([hour, count]) => (
                  <div key={hour} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span>{hour}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No redemption data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gift Passes Section */}
      {giftStats && (
        <Card className="border-pink-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-pink-600">
              <Gift className="w-5 h-5" />
              Gift Passes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-xl text-center">
                <p className="text-2xl font-bold text-pink-600">{giftStats.stats?.total_gifts || 0}</p>
                <p className="text-xs text-muted-foreground">Total Gifts Sold</p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl text-center">
                <p className="text-2xl font-bold text-green-600">{giftStats.stats?.redeemed || 0}</p>
                <p className="text-xs text-muted-foreground">Redeemed</p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-center">
                <p className="text-2xl font-bold text-amber-600">{giftStats.stats?.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
                <p className="text-2xl font-bold text-blue-600">{giftStats.stats?.redemption_rate || 0}%</p>
                <p className="text-xs text-muted-foreground">Redemption Rate</p>
              </div>
            </div>
            
            {giftStats.gifts?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground mb-2">Recent Gift Purchases</p>
                {giftStats.gifts.slice(0, 5).map((gift) => (
                  <div key={`gift-${gift.gift_code}`} className="flex items-center justify-between p-3 bg-pink-50/50 dark:bg-pink-900/10 rounded-lg border border-pink-200/50 dark:border-pink-800/30">
                    <div className="flex items-center gap-3">
                      <Share2 className="w-4 h-4 text-pink-500" />
                      <div>
                        <p className="font-medium text-sm">{gift.buyer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {gift.recipient_name ? `→ ${gift.recipient_name}` : 'Gift code generated'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <code className="text-xs font-mono bg-pink-100 dark:bg-pink-900/30 px-2 py-1 rounded">
                        {gift.gift_code}
                      </code>
                      <p className={`text-xs mt-1 ${gift.status === 'redeemed' ? 'text-green-600' : 'text-amber-600'}`}>
                        {gift.status === 'redeemed' ? '✓ Redeemed' : '⏳ Pending'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Redemptions Report - Admin Analytics (Not Customer-Facing) */}
      <Card className="border-teal-500/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-teal-600">
              <TrendingUp className="w-5 h-5" />
              Monthly Redemptions Report
            </CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="text-sm border rounded px-2 py-1 bg-background"
              >
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="text-sm border rounded px-2 py-1 bg-background"
              >
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchComprehensiveReport}
                disabled={loadingComprehensive}
              >
                {loadingComprehensive ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingComprehensive ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : comprehensiveReport?.monthly_leaderboard ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-teal-600">{comprehensiveReport.monthly_leaderboard.total_redemptions_this_month || 0}</p>
                  <p className="text-xs text-muted-foreground">Redemptions This Month</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{comprehensiveReport.monthly_leaderboard.total_upgrades_this_month || 0}</p>
                  <p className="text-xs text-muted-foreground">Upgrades This Month</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">
                Members sorted by redemption count for {comprehensiveReport.monthly_leaderboard.month}:
              </p>
              
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {comprehensiveReport.monthly_leaderboard.members_ranked_by_redemptions?.length > 0 ? (
                  comprehensiveReport.monthly_leaderboard.members_ranked_by_redemptions.map((member, idx) => (
                    <div key={`report-${member.pass_id || idx}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium text-sm">{member.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{member.email || member.phone || 'No contact'}</p>
                          {member.phone && member.email && (
                            <p className="text-xs text-muted-foreground">{member.phone}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-teal-600">{member.monthly_redemptions} this month</p>
                        <p className="text-xs text-muted-foreground">
                          {member.monthly_upgrades} size upgrades | {member.monthly_with_purchase} with food
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Total: {member.total_redemptions} all-time
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">No redemptions this month yet</p>
                )}
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-4">Select a month and click refresh to load data</p>
          )}
        </CardContent>
      </Card>

      {/* Member Analytics Summary */}
      {comprehensiveReport && (
        <Card className="border-purple-500/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-purple-600">
                <Users className="w-5 h-5" />
                All-Time Member Analytics
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportCsv}
                disabled={exportingCsv}
                className="gap-2"
              >
                {exportingCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-600">{comprehensiveReport.summary?.total_active_members || 0}</p>
                <p className="text-xs text-muted-foreground">Active Members</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{comprehensiveReport.summary?.total_redemptions_all_time || 0}</p>
                <p className="text-xs text-muted-foreground">Total Redemptions</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{comprehensiveReport.summary?.total_upgrades_all_time || 0}</p>
                <p className="text-xs text-muted-foreground">Total Upgrades</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{comprehensiveReport.summary?.total_orders_with_additional_items || 0}</p>
                <p className="text-xs text-muted-foreground">Orders w/ Food</p>
              </div>
            </div>
            
            {/* Average Spend Section */}
            <div className="bg-gradient-to-r from-teal-500/10 to-blue-500/10 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Average Member Spend (Since July 5, 2026)
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xl font-bold text-teal-600">${comprehensiveReport.average_spend?.average_per_member?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-muted-foreground">Avg per Member</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-blue-600">${comprehensiveReport.average_spend?.total_revenue_from_members?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-muted-foreground">Total Revenue</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-600">${comprehensiveReport.summary?.total_upgrade_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-muted-foreground">Upgrade Revenue</p>
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="border rounded-lg p-3">
                <p className="font-bold text-lg">{comprehensiveReport.top_redeemers?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Super Users (20+ redemptions)</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="font-bold text-lg">{comprehensiveReport.upgraders?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Members Who Upgraded</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="font-bold text-lg">{comprehensiveReport.additional_purchasers?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Members Who Bought Food</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Passes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Star className="w-5 h-5" />
            Recent Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          {passes.length > 0 ? (
            <div className="space-y-2">
              {passes.map((pass) => (
                <div key={`pass-${pass._id || pass.email}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="font-medium">{pass.first_name} {pass.last_name}</p>
                    <p className="text-sm text-muted-foreground">{pass.email}</p>
                    {pass.source && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        pass.source === 'pos' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        pass.source === 'comp' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {pass.source === 'pos' ? 'POS Sale' : pass.source === 'comp' ? 'Comped' : pass.source || 'Legacy'}
                      </span>
                    )}
                    {!pass.source && !pass.transaction_id && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        No Payment Record
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold">{pass.total_redemptions || 0} redemptions</p>
                      <p className="text-xs text-muted-foreground">
                        Expires {new Date(pass.expiry_date).toLocaleDateString()}
                      </p>
                    </div>
                    {/* Delete Button */}
                    {confirmDelete === pass._id ? (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeletePass(pass._id, `${pass.first_name} ${pass.last_name}`)}
                          disabled={deletingPassId === pass._id}
                          className="h-8 text-xs"
                          data-testid={`confirm-delete-pass-${pass._id}`}
                        >
                          {deletingPassId === pass._id ? 'Deleting...' : 'Confirm Delete'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDelete(null)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        {/* Cancel/Refund Button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setShowCancelModal(pass); resetCancelForm(); }}
                          className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                          title="Cancel & Refund"
                          data-testid={`cancel-pass-${pass._id}`}
                        >
                          <RefreshCcw className="w-4 h-4 mr-1" />
                          <span className="text-xs">Refund</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDelete(pass._id)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Delete pass permanently"
                          data-testid={`delete-pass-${pass._id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No members yet</p>
          )}
        </CardContent>
      </Card>
      </>
      )}
      
      {/* Edit Member Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Phone Number</Label>
              <Input
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                placeholder="+679..."
              />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input
                value={editForm.email || ''}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">First Name</Label>
                <Input
                  value={editForm.first_name || ''}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label className="text-sm">Last Name</Label>
                <Input
                  value={editForm.last_name || ''}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Status</Label>
              <select
                value={editForm.status || 'active'}
                onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cancel/Refund Modal */}
      <Dialog open={!!showCancelModal} onOpenChange={() => { setShowCancelModal(null); resetCancelForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCcw className="w-5 h-5" />
              Cancel Coffee Pass & Process Refund
            </DialogTitle>
            <DialogDescription>
              This will cancel the Coffee Pass, exclude it from revenue reports, and log the cancellation.
            </DialogDescription>
          </DialogHeader>
          
          {showCancelModal && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">{showCancelModal.first_name} {showCancelModal.last_name}</p>
                    <p className="text-sm text-muted-foreground">{showCancelModal.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Expires: {new Date(showCancelModal.expiry_date).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="font-heading text-xl">$99.00</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Reason for Cancellation *</Label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="cancel-pass-reason-select"
                >
                  <option value="">Select a reason...</option>
                  {CANCEL_REASONS.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Refund Method (optional)</Label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="cancel-pass-refund-method-select"
                >
                  <option value="">Not refunding yet</option>
                  {REFUND_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-sm">Additional Notes (optional)</Label>
                <Input
                  placeholder="Any additional details..."
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  data-testid="cancel-pass-notes"
                />
              </div>
              
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>This action will:</strong>
                </p>
                <ul className="text-xs text-amber-600 dark:text-amber-400 mt-1 space-y-1 list-disc list-inside">
                  <li>Mark Coffee Pass as cancelled (excluded from revenue)</li>
                  <li>Deactivate member&apos;s pass access</li>
                  <li>Update Coffee Pass sold count</li>
                  <li>Log cancellation with your name</li>
                </ul>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCancelPass}
                  disabled={processing || !cancelReason}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-cancel-pass-btn"
                >
                  {processing ? 'Processing...' : 'Confirm Cancellation'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowCancelModal(null); resetCancelForm(); }}
                >
                  Back
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complimentary Pass Modal - Simplified */}
      <Dialog open={showCompModal} onOpenChange={(open) => {
        setShowCompModal(open);
        if (!open) {
          setSelectedContact(null);
          setCompSearchQuery('');
          setCompSearchResults([]);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-purple-600" />
              Give Free Coffee Pass
            </DialogTitle>
            <DialogDescription>
              Search for an existing customer and give them a free Bula Coffee Pass.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by phone or email..."
                value={compSearchQuery}
                onChange={(e) => setCompSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCompSearch()}
                data-testid="comp-search-input"
              />
              <Button 
                onClick={handleCompSearch} 
                disabled={searchingContacts}
                variant="outline"
              >
                {searchingContacts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            
            {/* Search Results */}
            {compSearchResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {compSearchResults.map((contact, idx) => (
                  <div
                    key={contact.id || contact.contact_id || idx}
                    className={`p-3 cursor-pointer hover:bg-muted transition-colors ${
                      selectedContact?.id === contact.id || selectedContact?.email === contact.email 
                        ? 'bg-purple-50 dark:bg-purple-900/30 border-l-4 border-purple-500' 
                        : ''
                    }`}
                    onClick={() => setSelectedContact(contact)}
                  >
                    <p className="font-medium text-sm">{contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{contact.email}</p>
                    <p className="text-xs text-muted-foreground">{contact.phone}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Selected Contact */}
            {selectedContact && (
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="font-medium text-purple-700 dark:text-purple-300">
                  {selectedContact.name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-400">{selectedContact.email}</p>
                <p className="text-sm text-purple-600 dark:text-purple-400">{selectedContact.phone}</p>
                <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    Will receive: <strong>365-day Bula Coffee Pass ($95 value)</strong>
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    1 free hot coffee daily • 7-10 AM or 1-3:30 PM
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGiveCompPass}
              disabled={creatingComp || !selectedContact}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-comp-pass-btn"
            >
              {creatingComp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Give Pass
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
